// This is the app entry (renders the router), not a hot-reloaded component
// module, so react-refresh's "only export components" doesn't apply — the lazy
// route consts below would otherwise each trip it. Route components still HMR
// fine as their own lazy modules.
/* eslint-disable react-refresh/only-export-components */
import { StrictMode, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QuerySettingsProvider } from '@zenstackhq/tanstack-query/react';
import { Toaster } from 'sonner';
import App from './App';
// Eager — the app shell + first-paint / auth / legal pages (small, on the
// critical path). Everything else is lazy-loaded per route (see below) so the
// heavy feature pages (calendars, rehab, assistants, …) stay out of the initial
// bundle; the <Suspense> boundary that catches them lives in App.tsx around the
// router <Outlet/>.
import RootIndex from './RootIndex';
import HomePage from './pages/HomePage';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import PrivacyPage from './pages/Legal/Privacy';
import TermsPage from './pages/Legal/Terms';
import AboutPage from './pages/Legal/About';
import { AuthProvider } from './auth/AuthContext';
import { AuthGate } from './auth/AuthGate';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { authFetch } from './lib/api-fetch';
import { ThemeProvider } from './theme/ThemeContext';
import { GlobalLoadingBar } from './components/GlobalLoadingBar';
import '@fontsource-variable/inter';
import '@fontsource-variable/space-grotesk';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './i18n';
import './index.css';
// Eager — a new user's first impression (invite-accept flow); no Suspense flash.
import AcceptInvitePage from './pages/AcceptInvitePage';
import { AppUIProvider } from './components/AppUIProvider';
import { NotificationsProvider } from './notifications/NotificationsContext';

// Lazy-loaded feature pages — each becomes its own chunk, fetched on first
// navigation. Keeps the big ones (GroupEvents/EventDetail ~2.8k lines, the
// calendar, assistants) out of the initial bundle.
const AssistantsPage = lazy(() => import('./pages/Assistants'));
const AssistantWorkflowPage = lazy(() => import('./pages/AssistantWorkflow'));
const AssistantsFaqPage = lazy(() => import('./pages/AssistantsFaq'));
const UsersPage = lazy(() => import('./pages/Users'));
const AdminInvitePage = lazy(() => import('./pages/AdminInvitePage'));
const AdminCreateUserPage = lazy(() => import('./pages/AdminCreateUserPage'));
const SpecialistConfigPage = lazy(() => import('./pages/SpecialistConfig'));
const SpecialtiesPage = lazy(() => import('./pages/Specialties'));
const ServicesPage = lazy(() => import('./pages/Services'));
const GroupEventsPage = lazy(() => import('./pages/GroupEvents'));
const GroupEventDetailPage = lazy(() => import('./pages/GroupEvents/EventDetail'));
const CallsPage = lazy(() => import('./pages/Calls'));
const ContactsPage = lazy(() => import('./pages/Contacts'));
const ContactProfilePage = lazy(() => import('./pages/ContactProfile'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const InngestMonitorPage = lazy(() => import('./pages/InngestMonitor'));
const GeneralSettingsPage = lazy(() => import('./pages/GeneralSettings'));
const ActivityPage = lazy(() => import('./pages/Activity'));
const CreateResourcePage = lazy(() => import('./pages/CreateResource'));
const EditResourcePage = lazy(() => import('./pages/EditResource'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

const router = createBrowserRouter([
  // Public home page at the root — standalone, NO app shell/sidebar (anonymous
  // visitors and Google's OAuth review land here). Signed-in users are
  // redirected to the dashboard at /home.
  { path: '/', element: <RootIndex /> },
  {
    element: <App />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/about', element: <AboutPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      // Public — token-based, no JWT required.
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/home', element: <HomePage /> },
          { path: '/assistants', element: <AssistantsPage /> },
          { path: '/assistants/:id/workflow', element: <AssistantWorkflowPage /> },
          { path: '/superdoc/assistants', element: <AssistantsFaqPage /> },
          { path: '/calls', element: <CallsPage /> },
          { path: '/activity', element: <ActivityPage /> },
          { path: '/contacts', element: <ContactsPage /> },
          { path: '/contacts/:id', element: <ContactProfilePage /> },
          { path: '/appointments', element: <Navigate to="/calendar" replace /> },
          { path: '/calendar', element: <CalendarPage /> },
          { path: '/inngest', element: <InngestMonitorPage /> },
          { path: '/users', element: <UsersPage /> },
          { path: '/admin/invite', element: <AdminInvitePage /> },
          { path: '/admin/create-user', element: <AdminCreateUserPage /> },
          { path: '/admin/specialties', element: <SpecialtiesPage /> },
          { path: '/admin/services', element: <ServicesPage /> },
          { path: '/group-events', element: <GroupEventsPage /> },
          { path: '/group-events/:id', element: <GroupEventDetailPage /> },
          { path: '/general-settings', element: <GeneralSettingsPage /> },
          { path: '/specialist-config', element: <SpecialistConfigPage /> },
          { path: '/specialist-config/:userId', element: <SpecialistConfigPage /> },
          { path: '/create/:viewName', element: <CreateResourcePage /> },
          { path: '/edit/:viewName/:id', element: <EditResourcePage /> },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <GlobalLoadingBar />
        <AuthProvider>
          <QuerySettingsProvider value={{ endpoint: '/api', fetch: authFetch }}>
            <AppUIProvider>
              <NotificationsProvider>
                <AuthGate>
                  <RouterProvider router={router} />
                </AuthGate>
              </NotificationsProvider>
            </AppUIProvider>
            <Toaster position="top-right" closeButton />
          </QuerySettingsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
